import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Video } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminVideos() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [formData, setFormData] = useState({
    titulo: '',
    descricao: '',
    youtube_id: '',
    plan_required: 'FREE',
    categoria: '',
    thumbnail: ''
  });

  useEffect(() => {
    loadVideos();
  }, []);

  const loadVideos = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/videos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVideos(response.data);
    } catch (error) {
      toast.error('Erro ao carregar vídeos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      if (editingVideo) {
        await axios.put(`${BACKEND_URL}/api/admin/videos/${editingVideo.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Vídeo atualizado');
      } else {
        await axios.post(`${BACKEND_URL}/api/videos`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Vídeo criado');
      }
      
      setShowDialog(false);
      setEditingVideo(null);
      setFormData({ titulo: '', descricao: '', youtube_id: '', plan_required: 'FREE', categoria: '', thumbnail: '' });
      loadVideos();
    } catch (error) {
      toast.error('Erro ao salvar vídeo');
    }
  };

  const deleteVideo = async (videoId) => {
    if (!window.confirm('Tem certeza que deseja excluir este vídeo?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/admin/videos/${videoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Vídeo excluído');
      loadVideos();
    } catch (error) {
      toast.error('Erro ao excluir vídeo');
    }
  };

  const openEditDialog = (video) => {
    setEditingVideo(video);
    setFormData({
      titulo: video.titulo,
      descricao: video.descricao,
      youtube_id: video.youtube_id,
      plan_required: video.plan_required,
      categoria: video.categoria || '',
      thumbnail: video.thumbnail || ''
    });
    setShowDialog(true);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Gerenciar Vídeos
              </h1>
              <p className="text-muted-foreground text-lg">
                Total: {videos.length} vídeos
              </p>
            </div>
            <Button
              onClick={() => {
                setEditingVideo(null);
                setFormData({ titulo: '', descricao: '', youtube_id: '', plan_required: 'FREE', categoria: '', thumbnail: '' });
                setShowDialog(true);
              }}
              className="bg-gradient-to-r from-orange-500 to-red-500"
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Vídeo
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {videos.map((video) => (
              <div key={video.id} className="rounded-2xl overflow-hidden bg-white border border-border">
                <div className="relative aspect-video bg-muted">
                  <img
                    src={video.thumbnail || `https://img.youtube.com/vi/${video.youtube_id}/maxresdefault.jpg`}
                    alt={video.titulo}
                    className="w-full h-full object-cover"
                  />
                  {video.plan_required === 'PREMIUM' && (
                    <div className="absolute top-3 right-3 px-3 py-1 rounded-full bg-gradient-to-r from-primary to-secondary text-white text-xs font-semibold">
                      PREMIUM
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-semibold mb-2 line-clamp-2">{video.titulo}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{video.descricao}</p>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => openEditDialog(video)}
                      size="sm"
                      variant="outline"
                      className="flex-1"
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Editar
                    </Button>
                    <Button
                      onClick={() => deleteVideo(video.id)}
                      size="sm"
                      variant="destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingVideo ? 'Editar Vídeo' : 'Novo Vídeo'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Título</label>
              <Input
                value={formData.titulo}
                onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Descrição</label>
              <Input
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">ID do YouTube</label>
              <Input
                value={formData.youtube_id}
                onChange={(e) => setFormData({ ...formData, youtube_id: e.target.value })}
                placeholder="Ex: dQw4w9WgXcQ"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Categoria</label>
              <Input
                value={formData.categoria}
                onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Plano Necessário</label>
              <select
                value={formData.plan_required}
                onChange={(e) => setFormData({ ...formData, plan_required: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-border bg-white"
              >
                <option value="FREE">Grátis</option>
                <option value="PREMIUM">Premium</option>
              </select>
            </div>
            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)} className="flex-1">
                Cancelar
              </Button>
              <Button type="submit" className="flex-1 bg-gradient-to-r from-orange-500 to-red-500">
                {editingVideo ? 'Atualizar' : 'Criar'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
